import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({ options, selectedValue, onChange, placeholder, showAllOption = true, allOptionText = "All locations" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 250; // Approximate height of dropdown

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="border-2 border-gray-300 rounded-md px-3 py-2 cursor-pointer bg-white flex justify-between items-center hover:border-[#B02526] focus-within:border-[#B02526] focus-within:ring-2 focus-within:ring-[#B02526]/20 transition-all duration-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedValue === allOptionText ? 'text-gray-500' : 'text-black'}>
          {selectedValue || placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div 
          ref={dropdownListRef}
          className={`absolute left-0 right-0 bg-white border-2 border-[#B02526] rounded-md shadow-lg z-[9999] ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          style={{
            maxWidth: '100%',
            minWidth: '100%'
          }}
        >
          <input
            type="text"
            placeholder={`Search ${allOptionText.toLowerCase().includes('location') ? 'locations' : 'languages'}...`}
            className="w-full px-3 py-2 border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B02526]/20 focus:border-[#B02526] rounded-t-md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
          <div className="max-h-48 overflow-y-auto">
            {showAllOption && (
              <div
                className={`px-3 py-2 hover:bg-[#B02526]/10 cursor-pointer border-b border-gray-100 flex items-center justify-between ${
                  selectedValue === allOptionText ? 'bg-[#B02526]/5' : ''
                }`}
                onClick={() => handleSelect(allOptionText)}
              >
                <span>{allOptionText}</span>
                {selectedValue === allOptionText && (
                  <svg
                    className="w-4 h-4 text-[#B02526]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-3 py-2 hover:bg-[#B02526]/10 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center justify-between ${
                    selectedValue === option ? 'bg-[#B02526]/5' : ''
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  <span>{option}</span>
                  {selectedValue === option && (
                    <svg
                      className="w-4 h-4 text-[#B02526]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-center">
                No {allOptionText.toLowerCase().includes('location') ? 'locations' : 'languages'} found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;